import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { API } from "../../services/axios";
import { URLS } from "../../services/urls";
import { toast } from "react-toastify";
import { setUserDetails } from "../../components/utils/functions";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../store/slices/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faEnvelope, faLock } from "@fortawesome/free-solid-svg-icons";
import "./LoginPublic.css"; // Nouveau fichier CSS spécifique

const Login = () => {
  const [loading, setLoading] = useState(false);
  let [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({});
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onSubmit = (data) => {
    setLoading(true);
    API.post(URLS.AUTHS.LOGIN, data)
      .then((resp) => {
        // --- LOGIQUE DE REDIRECTION ROBUSTE (Comme l'ATS) ---
        const urlRedirect = searchParams.get("redirect");
        const localRedirect = window.localStorage.getItem("redirect");
        
        // Cible par défaut : Dashboard candidat
        let targetPath = "/dashboard"; 

        if (urlRedirect && !urlRedirect.includes("login")) {
          targetPath = urlRedirect;
        } else if (localRedirect && localRedirect !== "null" && localRedirect !== "undefined" && !localRedirect.includes("login")) {
          targetPath = localRedirect;
        }

        // Sécurité : toujours s'assurer que le chemin commence par "/"
        if (!targetPath.startsWith("/")) {
            targetPath = "/" + targetPath;
        }

        setLoading(false);
        let usrData = resp.data;
        // Adaptation pour ton backend si nécessaire (pk vs id)
        if(usrData.user && usrData.user.pk) {
            usrData.user.id = usrData.user.pk;
        }
        
        setUserDetails(usrData);
        dispatch(loginSuccess(usrData));
        navigate(targetPath);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(
          error?.response?.data?.detail ??
          error?.response?.data?.messagerror ??
          t("textLoginError")
        );
      });
  };

  return (
    <div className="public-login-wrapper">
      <div className="container h-100">
        <div className="row d-flex justify-content-center align-items-center h-100">
          <div className="col-12 col-md-9 col-lg-7 col-xl-5">
            
            {/* Carte de Connexion */}
            <div className="card public-card shadow-lg">
              <div className="card-body p-5">
                
                {/* En-tête avec Logo */}
                <div className="text-center mb-4">
                  <img
                    src="assets/imgs/page/homepage2/logo.png"
                    alt="Cible Emploi"
                    className="mb-3 logo-anim"
                    style={{ width: "160px" }}
                  />
                  <h4 className="fw-bold text-dark mt-2">{t("actionLogin")}</h4>
                  <p className="text-muted small">Bienvenue ! Accédez à vos opportunités.</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
                  
                  {/* Email */}
                  <div className="form-group mb-4">
                    <label className="form-label fw-semibold text-secondary small" htmlFor="email">
                      {t("email")}
                    </label>
                    <div className="input-group input-group-custom">
                        <span className="input-group-text">
                            <FontAwesomeIcon icon={faEnvelope} className="text-muted"/>
                        </span>
                        <input
                          className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                          id="email"
                          type="email"
                          placeholder="votre.email@exemple.com"
                          {...register("email", {
                            required: t("textFormFieldRequired"),
                            validate: {
                              maxLength: (v) => v.length <= 50 || t("textAtMost"),
                              matchPattern: (v) =>
                                /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || t("textValidEmail"),
                            },
                          })}
                        />
                    </div>
                    {errors.email?.message && (
                      <small className="text-danger mt-1">{errors.email.message}</small>
                    )}
                  </div>

                  {/* Mot de passe */}
                  <div className="form-group mb-4">
                    <div className="d-flex justify-content-between">
                        <label className="form-label fw-semibold text-secondary small" htmlFor="password">
                        {t("password")}
                        </label>
                    </div>
                    
                    <div className="input-group input-group-custom">
                        <span className="input-group-text">
                            <FontAwesomeIcon icon={faLock} className="text-muted"/>
                        </span>
                        <input
                            className="form-control"
                            id="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••••••"
                            {...register("password", { required: true })}
                        />
                        <button
                            type="button"
                            className="btn btn-link text-muted pe-3"
                            onClick={togglePasswordVisibility}
                            style={{textDecoration: 'none'}}
                        >
                            <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                        </button>
                    </div>
                    <div className="text-end mt-2">
                        <Link className="small text-primary fw-bold" to="/password-reset">
                            {t("forgotPassword")}
                        </Link>
                    </div>
                  </div>

                  {/* Bouton d'action */}
                  <div className="form-group mt-5">
                    <button
                      className="btn btn-primary btn-gradient w-100 py-3 fw-bold"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Connexion...
                        </>
                      ) : (
                        t("actionLogin")
                      )}
                    </button>
                  </div>

                  {/* Lien Inscription */}
                  <div className="text-center mt-4 pt-3 border-top">
                    <p className="text-muted small mb-0">{t("dontHaveAccount")}</p>
                    <Link to="/register" className="btn btn-outline-primary btn-sm mt-2 px-4 rounded-pill fw-bold">
                        {t("signUp")}
                    </Link>
                  </div>

                </form>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;